import { SerializationException } from '../exceptions/serialization-exception.js';
import { Buffer } from 'buffer';

export class BinaryReader {
  static readonly kBytesPerInt16: number = 2;
  static readonly kBytesPerInt32: number = 4;
  static readonly kBytesPerFloat32: number = 4;
  static readonly kBytesPerFloat64: number = 8;

  private buffer: Buffer;
  private byteOffset: number;

  constructor(buffer: Buffer) {
    this.buffer = buffer;
    this.byteOffset = 0;
  }

  static fromBytes(bytes: Uint8Array): BinaryReader {
    return new BinaryReader(Buffer.from(bytes));
  }

  skip(count: number): void {
    if(count >= 0) {
      this.byteOffset += count;
    }
  }

  seek(offset: number): void {
    this.byteOffset = Math.max(0, Math.min(offset, this.buffer.byteLength));
  }

  reset(): void {
    this.byteOffset = 0;
  }

  readBool(): boolean {
    const byte = this.readByte();
    return byte === 1;
  }

  peekByte(): number {
    if(this.byteOffset >= this.buffer.byteLength) {
      throw new SerializationException(`Byte offset ${this.byteOffset} out of range [0...${this.buffer.byteLength})`);
    }

    return this.buffer.readUInt8(this.byteOffset);
  }

  readByte(): number {
    if(this.byteOffset >= this.buffer.byteLength) {
      throw new SerializationException(`Byte offset ${this.byteOffset} out of range [0...${this.buffer.byteLength})`);
    }

    return this.buffer.readUInt8(this.byteOffset++);
  }

  readBytes(length: number): Uint8Array {
    if(this.byteOffset >= this.buffer.byteLength) {
      throw new SerializationException(`Byte offset ${this.byteOffset} out of range [0...${this.buffer.byteLength})`);
    }

    const bytes = new Uint8Array(length);
    for(let i = 0; i < length; i++) {
      bytes[i] = this.buffer.readUInt8(this.byteOffset++);
    }

    return bytes;
  }

  readUint8(): number {
    if(this.byteOffset >= this.buffer.byteLength) {
      throw new SerializationException(`Byte offset ${this.byteOffset} out of range [0...${this.buffer.byteLength})`);
    }

    return this.buffer.readUInt8(this.byteOffset++);
  }

  readUint16(): number {
    if(this.byteOffset >= this.buffer.byteLength) {
      throw new SerializationException(`Byte offset ${this.byteOffset} out of range [0...${this.buffer.byteLength})`);
    }

    const value = this.buffer.readUInt16BE(this.byteOffset);
    this.byteOffset += BinaryReader.kBytesPerInt16;

    return value;
  }

  readUint32(): number {
    if(this.byteOffset >= this.buffer.byteLength) {
      throw new SerializationException(`Byte offset ${this.byteOffset} out of range [0...${this.buffer.byteLength})`);
    }

    const value = this.buffer.readUInt32BE(this.byteOffset);
    this.byteOffset += BinaryReader.kBytesPerInt32;

    return value;
  }

  readInt8(): number {
    if(this.byteOffset >= this.buffer.byteLength) {
      throw new SerializationException(`Byte offset ${this.byteOffset} out of range [0...${this.buffer.byteLength})`);
    }

    return this.buffer.readInt8(this.byteOffset++);
  }

  readInt16(): number {
    if(this.byteOffset >= this.buffer.byteLength) {
      throw new SerializationException(`Byte offset ${this.byteOffset} out of range [0...${this.buffer.byteLength})`);
    }

    const value = this.buffer.readInt16BE(this.byteOffset);
    this.byteOffset += BinaryReader.kBytesPerInt16;

    return value;
  }

  readInt32(): number {
    if(this.byteOffset >= this.buffer.byteLength) {
      throw new SerializationException(`Byte offset ${this.byteOffset} out of range [0...${this.buffer.byteLength})`);
    }

    const value = this.buffer.readInt32BE(this.byteOffset);
    this.byteOffset += BinaryReader.kBytesPerInt32;

    return value;
  }

  readFloat32(): number {
    if(this.byteOffset >= this.buffer.byteLength) {
      throw new SerializationException(`Byte offset ${this.byteOffset} out of range [0...${this.buffer.byteLength})`);
    }

    const value = this.buffer.readFloatBE(this.byteOffset);
    this.byteOffset += BinaryReader.kBytesPerFloat32;

    return value;
  }

  readFloat64(): number {
    if(this.byteOffset >= this.buffer.byteLength) {
      throw new SerializationException(`Byte offset ${this.byteOffset} out of range [0...${this.buffer.byteLength})`);
    }

    const value = this.buffer.readDoubleBE(this.byteOffset);
    this.byteOffset += BinaryReader.kBytesPerFloat64;

    return value;
  }
}
